import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { ContentSkeleton } from 'src/components/ContentSkeleton';
import { useDeferredLoading } from 'src/hooks/useDeferredLoading';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { convertDate, formatFieldString, shortenString } from 'src/components/Table/helpers';
import {
  fetchTransactionCompliance,
  formatCaseReasonLabel,
  formatCaseUserName,
  formatOpenFor,
  formatOutcomeLabel,
  TransactionComplianceDetail,
} from 'src/api/adminCompliance';
import { formatKycStatusLabel, formatUserStatusLabel } from 'src/api/adminUsers';
import { ROUTE_USERS } from 'src/utils/routes';

import CopyableValue from './CopyableValue';

import {
  ActivityItem,
  ActivityList,
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from './styled';

type TransactionDetailsModalProps = {
  transaction: Record<string, any>;
  onClose: () => void;
};

const pick = (...values: unknown[]) =>
  values.find((v) => v !== undefined && v !== null && v !== '') as string | number | undefined;

const formatKind = (kind?: string) => {
  if (!kind) return 'Transaction';
  return formatFieldString(kind);
};

const formatAmount = (amount: unknown, currency?: string) => {
  if (amount === undefined || amount === null || amount === '') return null;
  const value = typeof amount === 'number' ? amount.toString() : String(amount);
  return currency ? `${value} ${currency}` : value;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({ transaction, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listId = String(pick(transaction.id, transaction._id) ?? '');

  const [loading, setLoading] = useState(Boolean(listId));
  const [detail, setDetail] = useState<TransactionComplianceDetail | null>(null);
  // Without this the skeleton is unmounted the moment the request resolves —
  // on a fast connection it never becomes visible.
  const showSkeleton = useDeferredLoading(loading, Boolean(detail));

  const load = useCallback(async () => {
    if (!listId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchTransactionCompliance(listId);
      setDetail(res);
    } catch (e: any) {
      // Keep list-row data visible; detail enrichment is best-effort.
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Could not load full transaction details',
          isSuccess: false,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [listId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const tx = detail?.transaction;
  const user = detail?.user ?? transaction.user;
  const screening = detail?.screening;
  const complianceCase = detail?.compliance_case;
  const activity = detail?.activity || [];

  const id = String(pick(tx?.id, listId) ?? '');
  const kind = String(pick(tx?.type, transaction.kind, transaction.type) ?? '');
  const status = String(pick(tx?.status, transaction.status) ?? '');
  const currency = String(pick(tx?.currency_code, transaction.currency_code, transaction.currency) ?? '');
  const amount = pick(tx?.amount, transaction.amount, transaction.value, transaction.quantity);
  const fee = pick(transaction.fee);
  const address = pick(
    tx?.address,
    transaction.address,
    transaction.recipient_address,
    transaction.destination_address,
  ) as string | undefined;
  const onChainHash = pick(
    tx?.transaction_hash,
    transaction.transaction_hash,
    transaction.blockchain_transaction_id,
    transaction.tx_hash,
    transaction.hash,
  ) as string | undefined;
  const vaultodyRef = pick(
    tx?.vaultody_reference,
    transaction.vaultody_reference,
    transaction.crypto_api_transaction_request_id,
    transaction.vaultody_ref,
  ) as string | undefined;
  const chain = pick(tx?.chain, tx?.blockchain, transaction.chain, transaction.network, transaction.blockchain) as
    | string
    | undefined;
  const createdAt = pick(tx?.created_at, transaction.created_at, transaction.requested_at) as string | undefined;
  const updatedAt = pick(tx?.updated_at, transaction.updated_at) as string | undefined;
  const heldSince = pick(tx?.held_since) as string | undefined;
  const heldForMs = tx?.held_for_ms;

  const userId = pick(user?.id, transaction.user_id) as string | undefined;
  const userName = formatCaseUserName(user);
  const userEmail = user?.email as string | undefined;

  const amountLabel = formatAmount(amount, currency);
  const feeLabel = formatAmount(fee, currency);
  const headline = [formatKind(kind), amountLabel].filter(Boolean).join(' · ') || 'Transaction';
  const addressLabel = String(kind).toUpperCase() === 'DEPOSIT' ? 'Sender / wallet address' : 'Destination address';

  const createdMeta = createdAt
    ? `Requested ${convertDate(createdAt).date}${convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}`
    : null;

  return (
    <Modal isOpen onClose={onClose}>
      {showSkeleton && !detail ? (
        <ContentSkeleton variant="detailModal" count={4} />
      ) : (
        <>
          <Breadcrumb>Transactions{id ? ` / ${shortenString(id)}` : ''}</Breadcrumb>

          <HeadlineRow>
            <TitleGroup>
              <Title>
                {headline}
                {status && <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>}
                {screening?.risk_level ? (
                  <StatusBadge $status={String(screening.risk_level)}>
                    {formatFieldString(String(screening.risk_level))}
                  </StatusBadge>
                ) : null}
              </Title>
              {createdMeta && <MetaLine>{createdMeta}</MetaLine>}
              {heldSince || heldForMs != null ? (
                <MetaLine>
                  {heldSince
                    ? `Held since ${convertDate(heldSince).date}${
                        convertDate(heldSince).time ? ` · ${convertDate(heldSince).time}` : ''
                      }`
                    : null}
                  {heldForMs != null ? `${heldSince ? ' · ' : ''}Held for ${formatOpenFor(heldForMs)}` : null}
                </MetaLine>
              ) : null}
            </TitleGroup>
          </HeadlineRow>

          {(userName || userId || userEmail) && (
            <>
              <Card>
                <CardTitle>User</CardTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Name</DetailLabel>
                    {userId ? (
                      <DetailValue title={userEmail ? `${userName} (${userEmail})` : userName}>
                        <UserLink
                          type="button"
                          onClick={() => {
                            onClose();
                            navigate(`${ROUTE_USERS}/${userId}`);
                          }}
                        >
                          {userName && userName !== '—' ? userName : shortenString(userId)}
                        </UserLink>
                      </DetailValue>
                    ) : (
                      <DetailValue>{userName || '—'}</DetailValue>
                    )}
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Email</DetailLabel>
                    <DetailValue $muted={!userEmail}>{userEmail || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>BxLend ID</DetailLabel>
                    <DetailValue $muted={!user?.bxlend_id}>{user?.bxlend_id || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Account status</DetailLabel>
                    <DetailValue>
                      {user?.status ? (
                        <StatusBadge $status={String(user.status)}>
                          {formatUserStatusLabel(user.status)}
                        </StatusBadge>
                      ) : (
                        '—'
                      )}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>KYC status</DetailLabel>
                    <DetailValue>
                      {user?.kyc_status ? (
                        <StatusBadge $status={String(user.kyc_status)}>
                          {formatKycStatusLabel(user.kyc_status)}
                        </StatusBadge>
                      ) : (
                        '—'
                      )}
                    </DetailValue>
                  </DetailItem>
                </DetailGrid>
              </Card>
              <SectionSpacer />
            </>
          )}

          <Card>
            <CardTitle>Transaction</CardTitle>
            <DetailGrid>
              {id ? (
                <DetailItem>
                  <DetailLabel>Transaction ID</DetailLabel>
                  <CopyableValue value={id} label="Transaction ID" />
                </DetailItem>
              ) : null}
              <DetailItem>
                <DetailLabel>Type</DetailLabel>
                <DetailValue>{formatKind(kind)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>
                  {status ? (
                    <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
                  ) : (
                    '—'
                  )}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Amount</DetailLabel>
                <DetailValue $muted={!amountLabel}>{amountLabel || '—'}</DetailValue>
              </DetailItem>
              {feeLabel || fee === 0 || fee === '0' ? (
                <DetailItem>
                  <DetailLabel>Fee</DetailLabel>
                  <DetailValue>{feeLabel || formatAmount(fee, currency) || '—'}</DetailValue>
                </DetailItem>
              ) : null}
              {currency ? (
                <DetailItem>
                  <DetailLabel>Currency</DetailLabel>
                  <DetailValue>{currency}</DetailValue>
                </DetailItem>
              ) : null}
              {chain ? (
                <DetailItem>
                  <DetailLabel>Network / chain</DetailLabel>
                  <DetailValue>{chain}</DetailValue>
                </DetailItem>
              ) : null}
              <DetailItem>
                <DetailLabel>{addressLabel}</DetailLabel>
                {address ? (
                  <CopyableValue value={address} label="Address" />
                ) : (
                  <DetailValue $muted>—</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>On-chain hash</DetailLabel>
                {onChainHash ? (
                  <CopyableValue value={onChainHash} label="On-chain hash" />
                ) : (
                  <DetailValue $muted>Not broadcast yet</DetailValue>
                )}
              </DetailItem>
              {vaultodyRef ? (
                <DetailItem>
                  <DetailLabel>Vaultody reference</DetailLabel>
                  <CopyableValue value={vaultodyRef} label="Vaultody reference" />
                </DetailItem>
              ) : null}
              {createdAt ? (
                <DetailItem>
                  <DetailLabel>Created</DetailLabel>
                  <DetailValue>
                    {convertDate(createdAt).date}
                    {convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}
                  </DetailValue>
                </DetailItem>
              ) : null}
              {updatedAt ? (
                <DetailItem>
                  <DetailLabel>Updated</DetailLabel>
                  <DetailValue>
                    {convertDate(updatedAt).date}
                    {convertDate(updatedAt).time ? ` · ${convertDate(updatedAt).time}` : ''}
                  </DetailValue>
                </DetailItem>
              ) : null}
            </DetailGrid>
          </Card>

          {screening ? (
            <>
              <SectionSpacer />
              <Card>
                <CardTitle>Screening</CardTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Risk</DetailLabel>
                    <DetailValue>
                      {screening.risk_level ? (
                        <StatusBadge $status={String(screening.risk_level)}>
                          {formatFieldString(String(screening.risk_level))}
                        </StatusBadge>
                      ) : (
                        '—'
                      )}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Provider risk</DetailLabel>
                    <DetailValue $muted={!screening.provider_risk}>
                      {screening.provider_risk || '—'}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue $muted={!screening.status}>
                      {screening.status ? formatFieldString(String(screening.status)) : '—'}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Recognised</DetailLabel>
                    <DetailValue>
                      {screening.recognised === false
                        ? 'No (no Chainalysis data)'
                        : screening.recognised
                          ? 'Yes'
                          : '—'}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Address type</DetailLabel>
                    <DetailValue $muted={!screening.address_type}>
                      {screening.address_type ? formatFieldString(String(screening.address_type)) : '—'}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Cluster</DetailLabel>
                    <DetailValue
                      $muted={!screening.cluster?.name && !screening.cluster?.category}
                    >
                      {[screening.cluster?.name, screening.cluster?.category].filter(Boolean).join(' · ') ||
                        '—'}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Reason</DetailLabel>
                    <DetailValue $muted={!screening.reason}>{screening.reason || '—'}</DetailValue>
                  </DetailItem>
                  {screening.screened_at ? (
                    <DetailItem>
                      <DetailLabel>Screened at</DetailLabel>
                      <DetailValue>
                        {convertDate(screening.screened_at).date}
                        {convertDate(screening.screened_at).time
                          ? ` · ${convertDate(screening.screened_at).time}`
                          : ''}
                      </DetailValue>
                    </DetailItem>
                  ) : null}
                  {(screening.direct_exposures?.length ?? 0) > 0 ? (
                    <DetailItem>
                      <DetailLabel>Direct exposures</DetailLabel>
                      <DetailValue>
                        {(screening.direct_exposures ?? [])
                          .map((ex) =>
                            [ex.category, ex.direction, ex.value != null ? String(ex.value) : null]
                              .filter(Boolean)
                              .join(' · '),
                          )
                          .join('; ')}
                      </DetailValue>
                    </DetailItem>
                  ) : null}
                </DetailGrid>
              </Card>
            </>
          ) : null}

          {complianceCase ? (
            <>
              <SectionSpacer />
              <Card>
                <CardTitle>Compliance case</CardTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Case</DetailLabel>
                    <DetailValue>#{complianceCase.case_number}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue>
                      {complianceCase.status ? (
                        <StatusBadge $status={String(complianceCase.status)}>
                          {formatFieldString(String(complianceCase.status))}
                        </StatusBadge>
                      ) : (
                        '—'
                      )}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Risk</DetailLabel>
                    <DetailValue>
                      {complianceCase.risk_level ? (
                        <StatusBadge $status={String(complianceCase.risk_level)}>
                          {formatFieldString(String(complianceCase.risk_level))}
                        </StatusBadge>
                      ) : (
                        '—'
                      )}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Reason</DetailLabel>
                    <DetailValue>
                      {formatCaseReasonLabel(complianceCase.reason, complianceCase.reason_detail)}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Outcome</DetailLabel>
                    <DetailValue $muted={!complianceCase.outcome}>
                      {formatOutcomeLabel(complianceCase.outcome ?? undefined)}
                    </DetailValue>
                  </DetailItem>
                  {complianceCase.opened_at ? (
                    <DetailItem>
                      <DetailLabel>Opened</DetailLabel>
                      <DetailValue>
                        {convertDate(complianceCase.opened_at).date}
                        {convertDate(complianceCase.opened_at).time
                          ? ` · ${convertDate(complianceCase.opened_at).time}`
                          : ''}
                      </DetailValue>
                    </DetailItem>
                  ) : null}
                  {complianceCase.resolved_at ? (
                    <DetailItem>
                      <DetailLabel>Resolved</DetailLabel>
                      <DetailValue>
                        {convertDate(complianceCase.resolved_at).date}
                        {convertDate(complianceCase.resolved_at).time
                          ? ` · ${convertDate(complianceCase.resolved_at).time}`
                          : ''}
                      </DetailValue>
                    </DetailItem>
                  ) : null}
                </DetailGrid>
              </Card>
            </>
          ) : null}

          {activity.length > 0 ? (
            <>
              <SectionSpacer />
              <Card>
                <CardTitle>Activity</CardTitle>
                <ActivityList>
                  {activity.map((entry, index) => (
                    <ActivityItem key={`${entry.occurred_at}-${index}`}>
                      <DetailValue>
                        {entry.event ? formatFieldString(String(entry.event)) : 'Event'}
                        {entry.occurred_at
                          ? ` · ${convertDate(String(entry.occurred_at)).date}${
                              convertDate(String(entry.occurred_at)).time
                                ? ` ${convertDate(String(entry.occurred_at)).time}`
                                : ''
                            }`
                          : ''}
                      </DetailValue>
                      {entry.message ? <MetaLine>{entry.message}</MetaLine> : null}
                      {entry.actor ? <MetaLine>Actor: {entry.actor}</MetaLine> : null}
                    </ActivityItem>
                  ))}
                </ActivityList>
              </Card>
            </>
          ) : null}
        </>
      )}
    </Modal>
  );
};

export default TransactionDetailsModal;
